/**
 * QR trang chủ SAPA có logo giữa (docs/plans/qr-sapa-va-nut-phan-hoi.md, mục 4.1) — phần thuần, không React.
 *
 * Mức sửa lỗi H (sửa được ~30%). Giữa mã khoét một vùng vuông cạnh lẻ rồi đặt logo; vùng này chỉ gồm ô DỮ LIỆU
 * (không chạm ô định vị / căn chỉnh / định dạng — đo bằng `encode().types`, xem qr-sapa.test.ts).
 * Địa chỉ là hằng, KHÔNG suy từ `location` (mở web quầy bằng địa chỉ nào cũng ra cùng một mã). Sinh ngay trên máy (uqr, ADR-037).
 */
import { TRANG_CHU_SAPA } from '@sapa/shared'
import { encode } from 'uqr'

/** Địa chỉ nằm ở `@sapa/shared` (trang-chu.ts, ADR-114); xuất lại để nơi nhập cũ không phải sửa. */
export { TRANG_CHU_SAPA }
export const MUC_SUA_LOI_QR_SAPA = 'H' as const
/** Vùng lặng chuẩn 4 ô quanh mã. */
export const VIEN_QR = 4

export interface QrSapa {
  svg: string
  coMaTran: number
  /** Vùng logo: ô đầu (cùng cho hàng và cột) và cạnh (số ô, lẻ). */
  oLogo: { tu: number; canh: number }
}

const MAU_TOI = '#1f1a1c'
const MAU_NEN = '#ffffff'
/** `--sp-primary` — logo tô đặc, không gradient (không có `id`/`url(#…)`). */
const MAU_LOGO = '#2f5fa7'

/** Cạnh vùng logo: số lẻ gần 0,27·n nhất, kẹp [7, 11]. */
function canhLogo(n: number): number {
  const lyTuong = 0.27 * n
  let tot = 7
  for (let c = 7; c <= 11; c += 2) if (Math.abs(c - lyTuong) < Math.abs(tot - lyTuong)) tot = c
  return tot
}

/** Ba nét vẽ của `Logo` (ui.tsx), hệ toạ độ 40×40, nền xanh đặc bo góc + các nét trắng. */
function veLogo(x: number, y: number, canh: number): string {
  const tiLe = canh / 40
  return (
    `<g transform="translate(${x} ${y}) scale(${tiLe})">` +
    `<rect width="40" height="40" rx="9" fill="${MAU_LOGO}"/>` +
    '<g transform="translate(6.8 6.6) scale(1.1)" fill="none" stroke="#fff" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">' +
    '<rect x="3.5" y="5" width="17" height="15.5" rx="2.6" fill="#fff" fill-opacity="0.14"/>' +
    '<path d="M8 3v4M16 3v4M3.5 10h17"/>' +
    '<path d="M8.6 15.1l2.3 2.3 4.6-4.6" stroke-width="2.2"/>' +
    '</g></g>'
  )
}

export function dungQrSapa(url: string = TRANG_CHU_SAPA): QrSapa {
  const ma = encode(url, { ecc: MUC_SUA_LOI_QR_SAPA, border: 0 })
  const n = ma.size
  const canh = canhLogo(n)
  const tu = (n - canh) / 2
  const trongLogo = (x: number, y: number) => x >= tu && x < tu + canh && y >= tu && y < tu + canh

  // Gộp các ô tối liền nhau trên một hàng thành một đoạn ngang, mọi đoạn nằm trong MỘT path.
  let d = ''
  for (let y = 0; y < n; y++) {
    let x = 0
    while (x < n) {
      if (ma.data[y]?.[x] === true && !trongLogo(x, y)) {
        let dai = 1
        while (x + dai < n && ma.data[y]?.[x + dai] === true && !trongLogo(x + dai, y)) dai++
        d += `M${x + VIEN_QR} ${y + VIEN_QR}h${dai}v1h-${dai}z`
        x += dai
      } else x++
    }
  }

  const rong = n + 2 * VIEN_QR
  const viTri = tu + VIEN_QR
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${rong} ${rong}" width="${rong}" height="${rong}" shape-rendering="crispEdges">` +
    `<rect width="${rong}" height="${rong}" fill="${MAU_NEN}"/>` +
    `<path d="${d}" fill="${MAU_TOI}"/>` +
    `<rect x="${viTri}" y="${viTri}" width="${canh}" height="${canh}" fill="${MAU_NEN}"/>` +
    veLogo(viTri + 1, viTri + 1, canh - 2) +
    '</svg>'
  return { svg, coMaTran: n, oLogo: { tu, canh } }
}

/** Phần của `Document` mà `tenBanDangChay` cần (để test không cần trình duyệt). */
export interface TaiLieuCoScript {
  querySelectorAll(chon: string): Iterable<{ getAttribute(ten: string): string | null }>
}

/**
 * Tên ngắn của bản web đang chạy: phần tên tệp (bỏ đuôi) của script `/assets/…` đầu tiên — Vite đặt mã băm vào tên.
 * Không có / dạng lạ → null. Dùng để gắn vào góp ý (nhóm B); chỉ gồm `[A-Za-z0-9_-]`, tối đa 40 ký tự.
 */
export function tenBanDangChay(
  doc: TaiLieuCoScript | undefined = typeof document === 'undefined' ? undefined : document,
): string | null {
  if (doc === undefined) return null
  for (const s of doc.querySelectorAll('script[src]')) {
    const src = s.getAttribute('src')
    if (src === null) continue
    const m = /\/assets\/([^/?#]+?)\.[A-Za-z0-9]+(?:[?#].*)?$/.exec(src)
    const ten = m?.[1]
    if (ten !== undefined && /^[A-Za-z0-9_-]+$/.test(ten)) return ten.slice(0, 40)
  }
  return null
}
